import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeOffIcon, RefreshCw } from 'lucide-react';
import { Mail, Lock, UserCircle, Phone } from 'lucide-react';
import CountryDropdown from './CountryDropdown';
import { countryCodes } from './constants';
import Loader from './Loader';
import InputField from './InputField';
import { AuthenticationProps, ProfileUpdateData, CountryCode } from './types';

function generateThreeWordPassword(wordList: string[]): string {
  const getRandomWord = () => wordList[Math.floor(Math.random() * wordList.length)];
  return `${getRandomWord()}-${getRandomWord()}-${getRandomWord()}`;
}

const Authentication: React.FC<AuthenticationProps> = ({
  currentUser,
  onLogin,
  onRegister,
  onLogout,
  onProfileUpdate,
  onPasswordRecovery,
  loading = false,
  error = null,
  wordList = [],
}) => {
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'recovery' | 'profile'>('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Registration fields
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(countryCodes[0]);
  const [showRegPassword, setShowRegPassword] = useState(true);

  // Profile fields
  const [profileFirstName, setProfileFirstName] = useState('');
  const [profileLastName, setProfileLastName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileCountry, setProfileCountry] = useState<CountryCode>(countryCodes[0]);

  // Recovery
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryMessage, setRecoveryMessage] = useState<string | null>(null);

  // Local error and loading
  const [localError, setLocalError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(false);

  // Password generator
  const handleGeneratePassword = () => {
    const newPassword = generateThreeWordPassword(wordList.length ? wordList : countryCodes.map(c => c.name));
    setRegPassword(newPassword);
    setRegConfirmPassword(newPassword);
    setShowRegPassword(true);
  };

  // Handlers
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalLoading(true);
    setLocalError(null);
    try {
      await onLogin?.(loginEmail, loginPassword);
    } catch (err: any) {
      setLocalError(err.message || 'Login failed');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalLoading(true);
    setLocalError(null);
    if (regPassword !== regConfirmPassword) {
      setLocalError('Passwords do not match');
      setLocalLoading(false);
      return;
    }
    try {
      await onRegister?.({
        email: regEmail,
        password: regPassword,
        firstName,
        lastName,
        phoneNumber,
        countryCode: selectedCountry.code,
      });
    } catch (err: any) {
      setLocalError(err.message || 'Registration failed');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalLoading(true);
    setLocalError(null);
    try {
      await onProfileUpdate?.({
        firstName: profileFirstName,
        lastName: profileLastName,
        phoneNumber: profilePhone,
        countryCode: profileCountry.code,
      });
    } catch (err: any) {
      setLocalError(err.message || 'Profile update failed');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalLoading(true);
    setRecoveryMessage(null);
    try {
      await onPasswordRecovery?.(recoveryEmail);
      setRecoveryMessage('If this email exists, a password reset link has been sent.');
    } catch (err: any) {
      setRecoveryMessage(err.message || 'Recovery failed');
    } finally {
      setLocalLoading(false);
    }
  };

  // Prefill profile fields if currentUser changes
  useEffect(() => {
    if (currentUser && authMode === 'profile') {
      setProfileFirstName(currentUser.firstName || '');
      setProfileLastName(currentUser.lastName || '');
      setProfilePhone(currentUser.phoneNumber || '');
      setProfileCountry(
        countryCodes.find(c => c.code === currentUser.countryCode) || countryCodes[0]
      );
    }
  }, [currentUser, authMode]);

  // UI
  if (loading || localLoading) {
    return <Loader />;
  }

  return (
    <div className="auth-card">
      <div className="auth-tab-container">
        <button className={`auth-tab ${authMode === 'login' ? 'auth-tab-active' : ''}`} onClick={() => setAuthMode('login')}>Login</button>
        <button className={`auth-tab ${authMode === 'register' ? 'auth-tab-active' : ''}`} onClick={() => setAuthMode('register')}>Register</button>
        <button className={`auth-tab ${authMode === 'recovery' ? 'auth-tab-active' : ''}`} onClick={() => setAuthMode('recovery')}>Recovery</button>
        {currentUser && (
          <button className={`auth-tab ${authMode === 'profile' ? 'auth-tab-active' : ''}`} onClick={() => setAuthMode('profile')}>Profile</button>
        )}
      </div>

      {authMode === 'login' && (
        <form onSubmit={handleLogin} className="auth-form">
          <InputField type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="Email" iconLeft={<Mail />} required />
          <InputField type={showLoginPassword ? "text" : "password"} value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="Password" iconLeft={<Lock />} required />
          <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)}>{showLoginPassword ? <EyeOffIcon /> : <EyeIcon />}</button>
          <button type="submit" className="auth-button-primary">Login</button>
        </form>
      )}

      {authMode === 'register' && (
        <form onSubmit={handleRegister} className="auth-form">
          <InputField type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="Email" iconLeft={<Mail />} required />
          <InputField type={showRegPassword ? "text" : "password"} value={regPassword} onChange={e => setRegPassword(e.target.value)} placeholder="Password" iconLeft={<Lock />} required />
          <button type="button" onClick={handleGeneratePassword}><RefreshCw /></button>
          <InputField type="password" value={regConfirmPassword} onChange={e => setRegConfirmPassword(e.target.value)} placeholder="Confirm Password" iconLeft={<Lock />} required />
          <InputField type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First Name" iconLeft={<UserCircle />} required />
          <InputField type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last Name" iconLeft={<UserCircle />} required />
          <InputField type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="Phone" iconLeft={<Phone />} required />
          <CountryDropdown selectedCountry={selectedCountry} onSelect={setSelectedCountry} />
          <button type="submit" className="auth-button-success">Register</button>
        </form>
      )}

      {authMode === 'recovery' && (
        <form onSubmit={handleRecovery} className="auth-form">
          <InputField type="email" value={recoveryEmail} onChange={e => setRecoveryEmail(e.target.value)} placeholder="Email for password recovery" iconLeft={<Mail />} required />
          <button type="submit" className="auth-button-warning">Send Recovery Email</button>
          {recoveryMessage && <div className="auth-message">{recoveryMessage}</div>}
        </form>
      )}

      {authMode === 'profile' && currentUser && (
        <form onSubmit={handleProfileSubmit} className="auth-form">
          <InputField type="text" value={profileFirstName} onChange={e => setProfileFirstName(e.target.value)} placeholder="First Name" iconLeft={<UserCircle />} required />
          <InputField type="text" value={profileLastName} onChange={e => setProfileLastName(e.target.value)} placeholder="Last Name" iconLeft={<UserCircle />} required />
          <InputField type="tel" value={profilePhone} onChange={e => setProfilePhone(e.target.value)} placeholder="Phone" iconLeft={<Phone />} required />
          <CountryDropdown selectedCountry={profileCountry} onSelect={setProfileCountry} />
          <button type="submit" className="auth-button-success">Update Profile</button>
        </form>
      )}

      {(localError || error) && <div className="auth-error-message">{localError || error}</div>}

      {currentUser && (
        <div className="auth-user-info">
          <div>Email: {currentUser.email}</div>
          <button onClick={onLogout} className="auth-button-danger">Logout</button>
        </div>
      )}
    </div>
  );
};

export default Authentication;
